import { useState } from 'react';
import { ChevronDown, LogOut, Settings, Shield, Smartphone } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import GroupSwitcher from '@/components/GroupSwitcher';
import ProfileModal from '@/components/ProfileModal';
import { UserAvatar } from '@/components/UserAvatar';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

interface MainFeedHeaderProps {
  groupId: string;
  groupName: string;
  isCommissioner?: boolean;
}

export const MainFeedHeader = ({ groupId, groupName, isCommissioner }: MainFeedHeaderProps) => {
  const { user, displayName, avatarId, signOut } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const { isIOS, isStandalone, isInstallable, openIOSModal, promptInstall } = useInstallPrompt();
  const showInstallMenuItem = !isStandalone && (isIOS || isInstallable);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto flex items-center justify-between h-14 px-4">
          {/* Right side (RTL): group switcher */}
          <GroupSwitcher currentGroupId={groupId} currentGroupName={groupName} />

          {/* Center: BRACKET RUN logo */}
          <Link to="/" className="text-lg font-bold tracking-tight absolute left-1/2 -translate-x-1/2">
            <img src="/images/logo.png" alt="Bracket Run" className="h-10 w-auto" />
          </Link>

          {/* Left side: user avatar dropdown */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 min-w-[44px] min-h-[44px] justify-center">
                  <UserAvatar avatarId={avatarId} displayName={displayName} size="sm" />
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-card border-border min-w-[160px]">
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-sm font-medium">{displayName || 'משתמש'}</p>
                </div>
                <DropdownMenuItem asChild>
                  <Link to="/bracket" className="gap-2 text-muted-foreground cursor-pointer flex items-center">
                    GLOBAL BRACKET
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/about" className="gap-2 text-muted-foreground cursor-pointer flex items-center">
                    איך זה עובד?
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setProfileOpen(true)} className="gap-2 text-muted-foreground cursor-pointer">
                  <Settings className="w-4 h-4" />
                  הגדרות
                </DropdownMenuItem>
                {isCommissioner && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate(`/group/${groupId}/manage`)}
                      className="gap-2 text-muted-foreground cursor-pointer"
                    >
                      <Shield className="w-4 h-4" />
                      ניהול קבוצה
                    </DropdownMenuItem>
                  </>
                )}
                {showInstallMenuItem && (
                  <DropdownMenuItem onClick={isIOS ? openIOSModal : promptInstall} className="gap-2 text-muted-foreground cursor-pointer">
                    <Smartphone className="w-4 h-4" />
                    התקינו את האפליקציה
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <a href="https://wa.me/972546339018?text=%D7%94%D7%99%D7%99%20%D7%90%D7%99%D7%99%D7%9C%2C%20%D7%99%D7%A9%20%D7%9C%D7%99%20%D7%91%D7%A2%D7%99%D7%94%20%D7%91-Bracket%20Run%3A%20" target="_blank" rel="noopener noreferrer" className="gap-2 text-muted-foreground cursor-pointer flex items-center">
                    <span>📬</span>
                    דווחו על בעיה
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOut} className="gap-2 text-muted-foreground cursor-pointer">
                  <LogOut className="w-4 h-4" />
                  התנתק
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>
      <ProfileModal open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
};

export default MainFeedHeader;
